import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { FaPlus } from 'react-icons/fa';
import NoRecordFound from '../../../components/common/no-record-found';
import { formatDateDDMMYYEmpty } from '../../../components/common/dateUtils';

const ROWS_PER_PAGE = 34;

const CustomsClearanceList = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(handler);
  }, [search]);

  // Fetch paginated data from server
  const fetchRecords = async (page, searchValue) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page,
        limit: ROWS_PER_PAGE,
        search: searchValue || '',
      });
      const res = await fetch(`${API_BASE_URL}/api/import/customs-clearance-get?${params.toString()}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const result = await res.json();
      setRecords(result.data || []);
      setTotal(result.total || 0);
    } catch (err) {
      setError('Failed to load data');
      console.error('Fetch customs clearance error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords(page, debouncedSearch);
    // eslint-disable-next-line
  }, [page, debouncedSearch]);

  const totalPages = Math.ceil(total / ROWS_PER_PAGE);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handlePrevPage = () => setPage((p) => Math.max(1, p - 1));
  const handleNextPage = () => setPage((p) => Math.min(totalPages, p + 1));

  return (
    <div className="max-w-8xl mx-auto p-2 bg-white rounded border border-gray-100">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-2">
        <h2 className="text-xl font-semibold">Customs Clearance</h2>
        <div className="flex gap-1 w-full sm:w-auto">
          <input
            type="text"
            placeholder="Search by order ID, bill of entry, status..."
            value={search}
            onChange={handleSearchChange}
            className="border border-gray-200 rounded px-2 py-1 focus:outline-none w-full sm:w-48 text-sm"
          />
          <button
            className="flex items-center gap-1 bg-[#3899cf] text-white px-3 py-1 text-xs rounded hover:bg-[#0e8dc6] border border-[#10a5e9] font-medium"
            onClick={() => navigate('/customs-clearance/new')}
          >
            <FaPlus />
            Add New
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-blue-600 py-6 text-sm">Loading...</div>
      ) : error ? (
        <div className="text-center text-red-600 py-6 text-sm">{error}</div>
      ) : records.length === 0 ? (
        <NoRecordFound message="No customs clearance records found." />
      ) : (
        <div className="overflow-x-auto rounded border border-gray-50">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Order ID</th>
                <th className="px-3 py-2 text-left font-medium">Contract No</th>
                <th className="px-3 py-2 text-left font-medium">Documents Release Date</th>
                <th className="px-3 py-2 text-left font-medium">Documents Handover Date</th>
                <th className="px-3 py-2 text-left font-medium">Clearance Date</th>
                <th className="px-3 py-2 text-left font-medium">PQ Status</th>
                <th className="px-3 py-2 text-left font-medium">FSSAI Status</th>
                <th className="px-3 py-2 text-left font-medium">Bill of Entry No</th>
                <th className="px-3 py-2 text-left font-medium">Bill of Entry Date</th>
                <th className="px-3 py-2 text-left font-medium">Duty Paid (USD)</th>
                <th className="px-3 py-2 text-left font-medium">Demurrage Amount</th>
                <th className="px-3 py-2 text-left font-medium">Remarks</th>
              </tr>
            </thead>
            <tbody>
              {records.map((record, idx) => (
                <tr key={record.order_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-3 py-1 font-semibold">{record.order_id}</td>
                  <td className="px-3 py-1">{record.purchase_contract_no}</td>
                  <td className="px-3 py-1">
                    {formatDateDDMMYYEmpty(record.documents_release_date)}
                  </td>
                  <td className="px-3 py-1">
                    {formatDateDDMMYYEmpty(record.documents_handover_date)}
                  </td>
                  <td className="px-3 py-1">
                    {formatDateDDMMYYEmpty(record.clearance_date)}
                  </td>
                  <td className="px-3 py-1">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      record.pq_status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                      record.pq_status === 'APPROVED' ? 'bg-blue-100 text-blue-800' :
                      record.pq_status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {record.pq_status || '-'}
                    </span>
                  </td>
                  <td className="px-3 py-1">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      record.fssai_status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                      record.fssai_status === 'APPROVED' ? 'bg-blue-100 text-blue-800' :
                      record.fssai_status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {record.fssai_status || '-'}
                    </span>
                  </td>
                  <td className="px-3 py-1">{record.bill_of_entry_no || '-'}</td>
                  <td className="px-3 py-1">
                    {formatDateDDMMYYEmpty(record.bill_of_entry_date) || '-'}
                  </td>
                  <td className="px-3 py-1">
                    {typeof record.duty_paid_usd === 'number'
                      ? record.duty_paid_usd.toFixed(2)
                      : record.duty_paid_usd || '-'}
                  </td>
                  <td className="px-3 py-1">
                    {typeof record.demurrage_amount === 'number'
                      ? record.demurrage_amount.toFixed(2)
                      : record.demurrage_amount || '-'}
                  </td>
                  <td className="px-3 py-1 max-w-xs truncate" title={record.remarks}>
                    {record.remarks || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination Controls */}
      {total > ROWS_PER_PAGE && (
        <div className="flex justify-end items-center gap-2 mt-3 text-xs">
          <button
            onClick={handlePrevPage}
            disabled={page === 1}
            className={`px-2 py-1 rounded border transition-colors duration-100 ${
              page === 1 
                ? 'bg-gray-100 text-gray-300 border-gray-200' 
                : 'bg-white text-[#3899cf] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'
            }`}
          >
            Prev
          </button>
          <span className="text-gray-600">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={handleNextPage}
            disabled={page === totalPages}
            className={`px-2 py-1 rounded border transition-colors duration-100 ${
              page === totalPages 
                ? 'bg-gray-100 text-gray-300 border-gray-200' 
                : 'bg-white text-[#3899cf] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'
            }`}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default CustomsClearanceList;
